import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { MAIN_SITE_URL } from "@/utils/constants";
import { generateArticleJsonLd, generateBreadcrumbJsonLd, generateFaqJsonLd } from "@/utils/seo-schema";

const articlePath = "/artykul/czy-kakobuy-jest-bezpieczny";
const articleImage = "https://readdy.ai/api/search-image?query=Minimalist%20workspace%20setup%20with%20laptop%20showing%20security%20shield%20icon%20on%20screen%2C%20warm%20cream%20desk%20surface%2C%20soft%20morning%20natural%20light%2C%20clean%20modern%20aesthetic%2C%20warm%20beige%20and%20sand%20tones%2C%20trust%20and%20protection%20concept%2C%20european%20lifestyle%20photography%2C%20calm%20and%20reassuring%20atmosphere%2C%20editorial%20composition&width=1200&height=800&seq=kakobuy-article-safety&orientation=landscape";

const schemas = [
  generateArticleJsonLd({
    headline: "Czy Kakobuy jest bezpieczny? Kompletny poradnik bezpieczeństwa dla polskich kupujących",
    description: "Wszystko, co musisz wiedzieć o bezpieczeństwie zakupów przez Kakobuy. Sprawdzone metody weryfikacji sprzedawców, bezpieczne płatności i ochrona przed oszustwami.",
    datePublished: "2026-07-01",
    dateModified: "2026-07-21",
    imageUrl: articleImage,
    articlePath,
  }),
  generateBreadcrumbJsonLd([
    { name: "Strona główna", path: "/" },
    { name: "Czy Kakobuy jest bezpieczny", path: articlePath },
  ]),
  generateFaqJsonLd([
    { question: "Czy mogę otrzymać zwrot pieniędzy jeśli produkt nie dotrze?", answer: "Tak, system ochrony kupujących Kakobuy umożliwia otwarcie sporu i uzyskanie zwrotu, jeśli przesyłka nie dotrze w określonym czasie." },
    { question: "Czy Kakobuy sprzedaje podróbki?", answer: "Kakobuy jest platformą pośredniczącą. Niektórzy sprzedawcy mogą oferować repliki. Zawsze sprawdzaj opisy produktów i oceny sprzedawcy." },
    { question: "Jak bezpiecznie płacić na Kakobuy?", answer: "Najbezpieczniejszą metodą jest płatność przez oficjalny system platformy — kartą kredytową lub przez portfele elektroniczne oferowane przez Kakobuy." },
  ]),
];

export default function CzyKakobuyBezpiecznyArticle() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-600 mb-8 flex-wrap">
              <Link to="/" className="hover:text-foreground-900 transition-colors cursor-pointer">Strona główna</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
              <span className="text-foreground-900 font-medium">Czy Kakobuy jest bezpieczny?</span>
            </nav>

            <header className="mb-10">
              <div className="inline-flex items-center gap-2 bg-accent-100 px-4 py-1.5 rounded-full mb-4">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-shield-check-line text-accent-500 text-sm"></i></span>
                <span className="text-xs font-semibold text-accent-700 whitespace-nowrap">Bezpieczeństwo</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                Czy Kakobuy jest bezpieczny? Kompletny poradnik bezpieczeństwa dla polskich kupujących
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Wszystko, co musisz wiedzieć o bezpieczeństwie zakupów przez Kakobuy. Sprawdzone metody weryfikacji sprzedawców, bezpieczne płatności i ochrona przed oszustwami.
              </p>
              <div className="flex items-center gap-3 text-xs text-foreground-600 flex-wrap">
                <span>Ostatnia aktualizacja: Lipiec 2026</span>
                <span className="text-foreground-400">•</span>
                <span>Czas czytania: 10 minut</span>
              </div>
            </header>

            <div className="w-full h-56 md:h-72 rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Minimalist%20workspace%20setup%20with%20laptop%20showing%20security%20shield%20icon%20on%20screen%2C%20warm%20cream%20desk%20surface%2C%20soft%20morning%20natural%20light%2C%20clean%20modern%20aesthetic%2C%20warm%20beige%20and%20sand%20tones%2C%20trust%20and%20protection%20concept%2C%20european%20lifestyle%20photography%2C%20calm%20and%20reassuring%20atmosphere%2C%20editorial%20composition&width=1200&height=800&seq=kakobuy-article-safety&orientation=landscape"
                alt="Czy Kakobuy jest bezpieczny — Poradnik"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-8">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Czy Kakobuy jest bezpieczny? Krótka odpowiedź</h2>
                <p className="mb-3">Tak, Kakobuy jest bezpieczną platformą do zakupów, pod warunkiem że przestrzegasz podstawowych zasad bezpieczeństwa. Platforma działa jako pośrednik między kupującymi a sprzedawcami, oferując system ochrony kupujących, możliwość kontroli jakości (QC) oraz bezpieczne metody płatności.</p>
                <p className="mb-3">Kluczowe jest jednak to, aby korzystać wyłącznie z oficjalnej strony Kakobuy i unikać podejrzanych linków czy fałszywych stron podszywających się pod platformę.</p>
                <p>W tym poradniku omówimy wszystkie aspekty bezpieczeństwa — od weryfikacji sprzedawców, przez bezpieczne płatności, aż po ochronę danych osobowych.</p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Jak Kakobuy chroni kupujących?</h2>
                <p className="mb-3">Platforma Kakobuy oferuje kilka mechanizmów ochrony, które znacząco zwiększają bezpieczeństwo zakupów. Oto najważniejsze z nich:</p>
                <div className="space-y-3">
                  {[
                    { t: "System ochrony kupujących", d: "Twoje pieniądze są bezpieczne do momentu potwierdzenia otrzymania zamówienia. Płatność jest przekazywana sprzedawcy dopiero po Twoim zatwierdzeniu." },
                    { t: "Zdjęcia kontroli jakości (QC)", d: "Przed wysyłką możesz poprosić o zdjęcia produktu. Dzięki temu widzisz rzeczywisty wygląd towaru i możesz zgłosić ewentualne problemy." },
                    { t: "Weryfikacja sprzedawców", d: "Platforma monitoruje sprzedawców i ich oceny. Możesz sprawdzić historię sprzedawcy przed dokonaniem zakupu." },
                    { t: "Bezpieczne metody płatności", d: "Kakobuy obsługuje sprawdzone metody płatności, w tym karty kredytowe i portfele elektroniczne, które oferują dodatkową warstwę ochrony." },
                  ].map((item) => (
                    <div key={item.t} className="bg-background-100 rounded-lg p-4 border border-background-200/70">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.t}</h3>
                      <p className="text-xs text-foreground-600">{item.d}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Najczęstsze zagrożenia i jak ich unikać</h2>
                <div className="bg-accent-100/50 border border-accent-200/60 rounded-xl p-5 mb-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-accent-200/60">
                          <th className="text-left py-2 pr-4 text-xs font-semibold text-foreground-700 whitespace-nowrap">Zagrożenie</th>
                          <th className="text-left py-2 text-xs font-semibold text-foreground-700 whitespace-nowrap">Jak unikać</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ["Fałszywe strony", "Zawsze sprawdzaj adres URL — oficjalna strona Kakobuy ma charakterystyczny wygląd i certyfikat SSL."],
                          ["Podejrzani sprzedawcy", "Sprawdzaj oceny i opinie. Unikaj sprzedawców z zerową historią i podejrzanie niskimi cenami."],
                          ["Kradzież danych", "Nigdy nie podawaj haseł ani danych karty poza oficjalną stroną. Używaj silnych, unikalnych haseł."],
                          ["Problemy z przesyłką", "Wybieraj wysyłkę z trackingiem i ubezpieczeniem. Dokumentuj rozpakowywanie przesyłki."],
                        ].map((row, i) => (
                          <tr key={i} className="border-b border-accent-200/60 last:border-0">
                            <td className="py-2.5 pr-4 text-foreground-800 font-medium whitespace-nowrap">{row[0]}</td>
                            <td className="py-2.5 text-foreground-600 text-xs">{row[1]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Lista kontrolna bezpiecznych zakupów</h2>
                <div className="space-y-2">
                  {[
                    "Sprawdź adres URL strony — czy to oficjalna domena Kakobuy?",
                    "Zweryfikuj oceny sprzedawcy i przeczytaj opinie innych kupujących.",
                    "Zawsze proś o zdjęcia QC przed zatwierdzeniem wysyłki.",
                    "Korzystaj wyłącznie z oficjalnych metod płatności platformy.",
                    "Nie wysyłaj pieniędzy bezpośrednio do sprzedawcy poza platformą.",
                    "Wybierz wysyłkę z numerem śledzenia i ubezpieczeniem.",
                    "Zachowaj całą korespondencję ze sprzedawcą w ramach platformy.",
                    "W przypadku problemów skontaktuj się z obsługą klienta Kakobuy.",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-background-100 rounded-lg border border-background-200/70">
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0 mt-0.5">
                        <span className="text-xs font-semibold text-accent-500">{i + 1}</span>
                      </span>
                      <span className="text-sm text-foreground-700">{item}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">FAQ — Najczęściej zadawane pytania</h2>
                <div className="space-y-3">
                  {[
                    { q: "Czy mogę otrzymać zwrot pieniędzy jeśli produkt nie dotrze?", a: "Tak, system ochrony kupujących Kakobuy umożliwia otwarcie sporu i uzyskanie zwrotu, jeśli przesyłka nie dotrze w określonym czasie." },
                    { q: "Czy Kakobuy sprzedaje podróbki?", a: "Kakobuy jest platformą pośredniczącą. Niektórzy sprzedawcy mogą oferować repliki. Zawsze sprawdzaj opisy produktów i oceny sprzedawcy." },
                    { q: "Jak bezpiecznie płacić na Kakobuy?", a: "Najbezpieczniejszą metodą jest płatność przez oficjalny system platformy — kartą kredytową lub przez portfele elektroniczne oferowane przez Kakobuy." },
                  ].map((faq, idx) => (
                    <details key={idx} className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden cursor-pointer">
                      <summary className="flex items-center justify-between p-4 text-sm font-semibold text-foreground-900 list-none">
                        {faq.q}
                        <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180"><i className="ri-arrow-down-s-line"></i></span>
                      </summary>
                      <div className="px-4 pb-4 text-sm text-foreground-700 leading-relaxed">{faq.a}</div>
                    </details>
                  ))}
                </div>
              </section>

              <section className="bg-gradient-to-r from-accent-100/60 to-primary-100/60 rounded-2xl p-6 md:p-8 text-center border border-accent-200/40">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">Zacznij bezpieczne zakupy już teraz</h2>
                <p className="text-sm text-foreground-700 mb-5 max-w-md mx-auto">Korzystaj z Kakobuy Spreadsheet — wszystkie linki prowadzą do sprawdzonych produktów na oficjalnej platformie.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto bg-accent-500 text-background-50 font-semibold px-8 py-3 rounded-full hover:bg-accent-600 transition-all text-sm whitespace-nowrap cursor-pointer">Otwórz Kakobuy Spreadsheet</a>
                  <Link to="/" className="w-full sm:w-auto bg-background-50 text-foreground-900 font-semibold px-8 py-3 rounded-full border border-background-300/60 hover:bg-background-100 transition-all text-sm whitespace-nowrap cursor-pointer">Powrót na stronę główną</Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: s }} />
      ))}
    </div>
  );
}